import { useEffect, useState } from "react";
import { api, fmtEur, formatErr } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../lib/auth";
import { toast } from "sonner";

export default function NewServiceDialog({ open, onOpenChange, onCreated }) {
  const { user, isSolo } = useAuth();
  const [users, setUsers] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [vouchers, setVouchers] = useState([]);
  const [form, setForm] = useState({
    license_plate: "", wash_type: "",
    total_price: 0, operario_id: "", status: "Pendiente",
    payment_method: "cash", customer_voucher_id: null,
    customer_type: "particular", razon_social: "", nif_cif: "", address_fiscal: "",
    // Sprint D · Factura Pro · desglose Mano de Obra vs Materiales
    pro_breakdown: false,
    labor_amount: "",
    parts_amount: "",
    // Sprint H · Batch 9.2 — itemizado con descripción
    labor_detail: [],   // [{ name, qty, price }]
    parts_detail: [],   // [{ name, qty, price }]
    // Sprint H · Batch 9.1 — descuento porcentual (0-100)
    discount_percentage: "",
  });
  const [busy, setBusy] = useState(false);
  // Sprint D · prepaid-status unificado (vouchers + memberships)
  const [prepaid, setPrepaid] = useState({ vouchers: [], membership: null, has_prepaid: false });

  // Cuando cambia la matrícula, busca prepago unificado (vouchers + memberships)
  useEffect(() => {
    if (!form.license_plate || form.license_plate.length < 4) {
      setVouchers([]); setPrepaid({ vouchers: [], membership: null, has_prepaid: false });
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/customers/prepaid-status/${encodeURIComponent(form.license_plate)}`);
        setPrepaid(data);
        setVouchers(data.vouchers || []);
      } catch { setVouchers([]); setPrepaid({ vouchers: [], membership: null, has_prepaid: false }); }
    }, 350);
    return () => clearTimeout(t);
  }, [form.license_plate]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      const tasks = [api.get("/catalog")];
      if (user.role === "admin") tasks.push(api.get("/users"));
      const results = await Promise.all(tasks);
      const cat = results[0].data;
      setCatalog(cat);
      if (user.role === "admin") {
        setUsers(results[1].data);
        // In solo mode, default operario to the admin (themself)
        const defaultOperario = isSolo
          ? user.id
          : (results[1].data[0]?.id || user.id);
        setForm((f) => ({
          ...f,
          operario_id: f.operario_id || defaultOperario,
          wash_type: f.wash_type || cat[0]?.name || "",
          total_price: f.total_price || cat[0]?.price || 0,
        }));
      } else {
        setForm((f) => ({
          ...f,
          operario_id: user.id,
          wash_type: f.wash_type || cat[0]?.name || "",
          total_price: f.total_price || cat[0]?.price || 0,
        }));
      }
    })();
  }, [open, user, isSolo]);

  const onPickType = (name) => {
    const item = catalog.find((c) => c.name === name);
    setForm((f) => ({ ...f, wash_type: name, total_price: item ? item.price : f.total_price }));
  };

  const base = (Number(form.total_price) || 0) / 1.21;
  const iva = (Number(form.total_price) || 0) - base;

  const submit = async () => {
    setBusy(true);
    try {
      const isEmpresa = form.customer_type === "empresa";
      let customer_id = null;
      // Si es Empresa, creamos/actualizamos el cliente CRM con datos fiscales para emitir Factura completa.
      if (isEmpresa) {
        if (!form.razon_social.trim() || !form.nif_cif.trim()) {
          toast.error("Empresa: indica Razón Social y CIF/NIF para emitir factura completa");
          setBusy(false);
          return;
        }
        const { data } = await api.post("/customers", {
          type: "empresa",
          name: form.razon_social.trim(),
          razon_social: form.razon_social.trim(),
          nif_cif: form.nif_cif.trim().toUpperCase(),
          address_fiscal: form.address_fiscal.trim(),
          main_license_plate: form.license_plate.trim().toUpperCase(),
        });
        customer_id = data.id;
      }
      // Validación desglose Pro
      let labor_amount = null, parts_amount = null;
      let labor_detail = null, parts_detail = null;
      if (form.pro_breakdown) {
        // Sumas autocalculadas a partir del detalle itemizado (si hay líneas)
        const sumLines = (rows) => rows.reduce((acc, r) => acc + (Number(r.qty) || 1) * (Number(r.price) || 0), 0);
        const laborSum = form.labor_detail.length > 0 ? sumLines(form.labor_detail) : (Number(form.labor_amount) || 0);
        const partsSum = form.parts_detail.length > 0 ? sumLines(form.parts_detail) : (Number(form.parts_amount) || 0);
        labor_amount = Number(laborSum.toFixed(2));
        parts_amount = Number(partsSum.toFixed(2));
        labor_detail = form.labor_detail.filter((r) => r.name && Number(r.price) > 0);
        parts_detail = form.parts_detail.filter((r) => r.name && Number(r.price) > 0);
        if (Math.abs((labor_amount + parts_amount) - Number(form.total_price)) > 0.5) {
          toast.error("Mano de obra + Materiales no coincide con el total");
          setBusy(false);
          return;
        }
      }
      await api.post("/services", {
        license_plate: form.license_plate,
        wash_type: form.wash_type,
        total_price: Number(form.total_price),
        operario_id: form.operario_id,
        status: form.status,
        customer_id,
        payment_method: form.payment_method,
        customer_voucher_id: form.payment_method === "voucher" ? form.customer_voucher_id : null,
        labor_amount, parts_amount,
        labor_detail, parts_detail,
        discount_percentage: form.discount_percentage !== "" ? Number(form.discount_percentage) : null,
      });
      toast.success(isEmpresa ? "Servicio registrado · Factura completa lista" : "Servicio registrado · Ticket simplificado");
      onOpenChange(false);
      setForm((f) => ({ ...f, license_plate: "", status: "Pendiente", razon_social: "", nif_cif: "", address_fiscal: "" }));
      onCreated?.();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-md" data-testid="new-service-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tighter">Nuevo servicio</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {/* Tipo de cliente — Particular (ticket) / Empresa (factura completa) */}
          <div>
            <Label className="text-xs uppercase tracking-wider">Tipo de cliente</Label>
            <Select value={form.customer_type} onValueChange={(v)=>setForm({...form, customer_type: v})}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="ns-customer-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="particular" data-testid="ns-type-particular">👤 Particular · Ticket simplificado</SelectItem>
                <SelectItem value="empresa" data-testid="ns-type-empresa">🏢 Empresa · Factura completa</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-[10px] text-[var(--ag-text-dim)] mt-1">
              {form.customer_type === "empresa"
                ? "Se generará Factura completa con NIF y razón social — exigible por la Agencia Tributaria."
                : "Se generará Ticket simplificado (Reglamento de Facturación · servicios &lt; 400€)."}
            </p>
          </div>
          {/* Datos fiscales (solo Empresa) */}
          {form.customer_type === "empresa" && (
            <div className="border border-[var(--ag-aqua)]/30 bg-[var(--ag-aqua)]/5 p-3 rounded-sm space-y-2" data-testid="ns-empresa-fields">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-aqua-soft)] font-bold">Datos fiscales empresa *</div>
              <EmpresaAutocomplete
                value={form.razon_social}
                onPick={(c) => setForm({
                  ...form,
                  razon_social: c.razon_social || c.name || form.razon_social,
                  nif_cif: c.nif_cif || form.nif_cif,
                  address_fiscal: c.address_fiscal || c.address || form.address_fiscal,
                })}
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Razón social *</Label>
                  <Input value={form.razon_social} onChange={(e)=>setForm({...form, razon_social: e.target.value})}
                    className="mt-1 rounded-sm" placeholder="Ej. Acme SL" data-testid="ns-razon-social" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">CIF / NIF *</Label>
                  <Input value={form.nif_cif} onChange={(e)=>setForm({...form, nif_cif: e.target.value.toUpperCase()})}
                    className="mt-1 rounded-sm font-mono-tab" placeholder="B12345678" data-testid="ns-nif-cif" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Dirección fiscal</Label>
                <Input value={form.address_fiscal} onChange={(e)=>setForm({...form, address_fiscal: e.target.value})}
                  className="mt-1 rounded-sm" placeholder="C/ ..." data-testid="ns-address-fiscal" />
              </div>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider">Matrícula</Label>
            <Input value={form.license_plate} onChange={(e)=>setForm({...form, license_plate: e.target.value.toUpperCase()})}
              className="mt-1 rounded-sm font-mono-tab tracking-wider" placeholder="1234ABC" data-testid="ns-plate" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Tipo de servicio (catálogo)</Label>
            <Select value={form.wash_type} onValueChange={onPickType}>
              <SelectTrigger className="mt-1 rounded-sm" data-testid="ns-type"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>
                {catalog.map((c)=> (
                  <SelectItem key={c.id} value={c.name}>
                    {c.name} · {fmtEur(c.price)}
                    {c.price_min != null && c.price_max != null ? ` (rango)` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Total (IVA incl.)</Label>
              <Input type="number" min={0} step="0.01" value={form.total_price}
                onChange={(e)=>setForm({...form, total_price: e.target.value})}
                className="mt-1 rounded-sm tabular-nums" data-testid="ns-price" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Estado</Label>
              <Select value={form.status} onValueChange={(v)=>setForm({...form, status: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="ns-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Pendiente">Borrador (editable)</SelectItem>
                  <SelectItem value="Pagado">Emitida (inalterable)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {user.role === "admin" && !isSolo && (
            <div>
              <Label className="text-xs uppercase tracking-wider">Operario</Label>
              <Select value={form.operario_id} onValueChange={(v)=>setForm({...form, operario_id: v})}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="ns-operario"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {users.map((u)=> <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Sprint D · Factura Pro · Sprint H Batch 9.2 — desglose itemizado Mano de Obra + Materiales */}
          <div className="border border-[var(--ag-gold)]/30 bg-[var(--ag-gold)]/5 rounded-sm p-3 space-y-3" data-testid="ns-pro-breakdown-card">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={form.pro_breakdown}
                onChange={(e)=>setForm({...form, pro_breakdown: e.target.checked})}
                className="accent-[#0033A0]"
                data-testid="ns-pro-toggle" />
              <span className="font-semibold text-[var(--ag-gold)]">Factura Pro · desglose Mano de Obra / Materiales</span>
            </label>
            {form.pro_breakdown && (() => {
              const sumLines = (rows) => rows.reduce((acc, r) => acc + (Number(r.qty) || 1) * (Number(r.price) || 0), 0);
              const laborTotal = form.labor_detail.length > 0 ? sumLines(form.labor_detail) : (Number(form.labor_amount) || 0);
              const partsTotal = form.parts_detail.length > 0 ? sumLines(form.parts_detail) : (Number(form.parts_amount) || 0);
              const grand = laborTotal + partsTotal;
              const matchOk = Math.abs(grand - Number(form.total_price)) <= 0.5;

              const renderItemTable = (kind, rows, accent, addLabel, testidPrefix) => (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className={`text-[11px] uppercase tracking-wider font-semibold ${accent}`}>
                      {kind === "labor" ? "Mano de obra · líneas detalladas" : "Materiales / Recambios · líneas detalladas"}
                    </Label>
                    <button type="button"
                      onClick={()=>setForm((f)=>({ ...f, [`${kind}_detail`]: [...f[`${kind}_detail`], { name: "", qty: 1, price: "" }] }))}
                      className="text-[11px] font-semibold text-[var(--ag-gold)] hover:underline"
                      data-testid={`${testidPrefix}-add`}>
                      + {addLabel}
                    </button>
                  </div>
                  {rows.length === 0 && (
                    <div className="text-[10px] text-gray-500 italic">
                      Sin líneas detalladas — usa el campo de importe agregado abajo o añade líneas con "+ {addLabel}".
                    </div>
                  )}
                  {rows.map((row, idx) => (
                    <div key={idx} className="grid grid-cols-[1fr_60px_80px_28px] gap-2 items-center" data-testid={`${testidPrefix}-row-${idx}`}>
                      <Input type="text" placeholder={kind === "labor" ? "Concepto (ej: Cambio aceite)" : "Material (ej: Aceite 5W30 4L)"}
                        value={row.name}
                        onChange={(e)=>setForm((f)=>{
                          const next = [...f[`${kind}_detail`]];
                          next[idx] = { ...next[idx], name: e.target.value };
                          return { ...f, [`${kind}_detail`]: next };
                        })}
                        className="rounded-sm text-xs h-8"
                        data-testid={`${testidPrefix}-name-${idx}`} />
                      <Input type="number" min={1} step={1} placeholder="Cant"
                        value={row.qty}
                        onChange={(e)=>setForm((f)=>{
                          const next = [...f[`${kind}_detail`]];
                          next[idx] = { ...next[idx], qty: e.target.value };
                          return { ...f, [`${kind}_detail`]: next };
                        })}
                        className="rounded-sm text-xs h-8 tabular-nums text-center"
                        data-testid={`${testidPrefix}-qty-${idx}`} />
                      <Input type="number" min={0} step="0.01" placeholder="€/u"
                        value={row.price}
                        onChange={(e)=>setForm((f)=>{
                          const next = [...f[`${kind}_detail`]];
                          next[idx] = { ...next[idx], price: e.target.value };
                          return { ...f, [`${kind}_detail`]: next };
                        })}
                        className="rounded-sm text-xs h-8 tabular-nums text-right"
                        data-testid={`${testidPrefix}-price-${idx}`} />
                      <button type="button"
                        onClick={()=>setForm((f)=>({ ...f, [`${kind}_detail`]: f[`${kind}_detail`].filter((_, i) => i !== idx) }))}
                        className="text-red-500 hover:text-red-700 text-base font-bold leading-none"
                        aria-label="Eliminar línea"
                        data-testid={`${testidPrefix}-remove-${idx}`}>
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              );

              return (
                <div className="space-y-4 pt-1">
                  {/* Mano de obra */}
                  <div>
                    {renderItemTable("labor", form.labor_detail, "text-[var(--ag-gold)]", "Añadir línea", "ns-labor")}
                    {form.labor_detail.length === 0 && (
                      <div className="mt-2">
                        <Label className="text-[10px] uppercase tracking-wider text-gray-600">Mano de obra — importe agregado (€)</Label>
                        <Input type="number" min={0} step="0.01" value={form.labor_amount}
                          onChange={(e)=>setForm({...form, labor_amount: e.target.value})}
                          className="mt-1 rounded-sm tabular-nums max-w-[160px]" data-testid="ns-labor" placeholder="40.00" />
                      </div>
                    )}
                    {form.labor_detail.length > 0 && (
                      <div className="text-[10px] text-[var(--ag-gold)] mt-1.5 text-right font-semibold">
                        Subtotal Mano de obra: {fmtEur(laborTotal)}
                      </div>
                    )}
                  </div>

                  {/* Materiales */}
                  <div>
                    {renderItemTable("parts", form.parts_detail, "text-[var(--ag-gold)]", "Añadir material", "ns-parts")}
                    {form.parts_detail.length === 0 && (
                      <div className="mt-2">
                        <Label className="text-[10px] uppercase tracking-wider text-gray-600">Materiales — importe agregado (€)</Label>
                        <Input type="number" min={0} step="0.01" value={form.parts_amount}
                          onChange={(e)=>setForm({...form, parts_amount: e.target.value})}
                          className="mt-1 rounded-sm tabular-nums max-w-[160px]" data-testid="ns-parts" placeholder="80.00" />
                      </div>
                    )}
                    {form.parts_detail.length > 0 && (
                      <div className="text-[10px] text-[var(--ag-gold)] mt-1.5 text-right font-semibold">
                        Subtotal Materiales: {fmtEur(partsTotal)}
                      </div>
                    )}
                  </div>

                  <div className={`text-[11px] font-semibold flex justify-between border-t pt-2 ${matchOk ? 'text-emerald-700 border-emerald-200' : 'text-amber-700 border-amber-200'}`}>
                    <span>Suma desglose</span>
                    <span data-testid="ns-pro-grand-total">{fmtEur(grand)} {matchOk ? "✓" : `≠ ${fmtEur(form.total_price)}`}</span>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Sprint H · Batch 9.1 — Descuento porcentual sobre la base imponible */}
          <div className="border border-emerald-300/60 bg-emerald-50 rounded-sm p-3" data-testid="ns-discount-card">
            <Label htmlFor="ns-discount" className="text-xs uppercase tracking-wider text-emerald-800 font-semibold">
              Descuento sobre factura (%)
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                id="ns-discount"
                type="number"
                min={0}
                max={100}
                step="0.1"
                value={form.discount_percentage}
                onChange={(e)=>setForm({...form, discount_percentage: e.target.value})}
                className="rounded-sm tabular-nums max-w-[120px]"
                placeholder="0"
                data-testid="ns-discount-input"
              />
              <span className="text-sm text-emerald-800 font-semibold">%</span>
              {form.discount_percentage && Number(form.discount_percentage) > 0 && (
                <span className="text-[11px] text-emerald-700 ml-auto">
                  −{fmtEur((Number(form.total_price)||0) * (Number(form.discount_percentage)||0) / 100)} aplicado
                </span>
              )}
            </div>
            <p className="text-[10px] text-emerald-700/80 mt-1.5">
              Opcional. Aparecerá en el PDF como una línea de descuento separada (Base · Descuento (X%) · Imponible · IVA · TOTAL).
            </p>
          </div>

          {/* Chip de prepago detectado (vouchers + memberships unificados) */}
          {prepaid.has_prepaid && (
            <div className="border border-purple-300 bg-purple-50 rounded-sm p-3 text-xs space-y-2" data-testid="ns-prepaid-chip">
              {prepaid.membership && (
                <div className="text-purple-900 font-semibold flex items-center gap-1">💎 Membresía activa: {prepaid.membership.plan_name || "Stripe"}</div>
              )}
              {prepaid.vouchers.length > 0 && (
                <div className="text-purple-900 flex items-center gap-1">🎟 {prepaid.vouchers.length} bono(s) con saldo disponible</div>
              )}
              <div className="pt-1">
                <Label className="text-[10px] uppercase tracking-wider text-purple-800">Forma de pago</Label>
                <Select value={form.payment_method} onValueChange={(v)=>setForm({...form, payment_method: v, customer_voucher_id: null})}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white" data-testid="ns-payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash" data-testid="ns-pay-cash">💶 Efectivo / TPV</SelectItem>
                    {prepaid.vouchers.length > 0 && (
                      <SelectItem value="voucher" data-testid="ns-pay-voucher">🎟 Bono prepago</SelectItem>
                    )}
                    {prepaid.membership && (
                      <SelectItem value="membership" data-testid="ns-pay-membership">💎 Membresía Stripe</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {form.payment_method === "voucher" && prepaid.vouchers.length > 0 && (
                  <Select value={form.customer_voucher_id || ""} onValueChange={(v)=>setForm({...form, customer_voucher_id: v})}>
                    <SelectTrigger className="mt-2 rounded-sm bg-white" data-testid="ns-voucher-pick"><SelectValue placeholder="Elige bono" /></SelectTrigger>
                    <SelectContent>
                      {prepaid.vouchers.map((v) => (
                        <SelectItem key={v.id} value={v.id} data-testid={`ns-voucher-${v.id}`}>
                          {v.voucher_name || "Bono"} · {v.washes_remaining} restantes
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          )}

          <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 text-xs tabular-nums space-y-1">
            <div className="flex justify-between"><span className="text-gray-500">Base Imponible (Total/1.21)</span><span>{fmtEur(base)}</span></div>
            <div className="flex justify-between"><span className="text-gray-500">IVA 21%</span><span>{fmtEur(iva)}</span></div>
            <div className="flex justify-between font-semibold"><span>Total</span><span>{fmtEur(form.total_price)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>onOpenChange(false)} className="rounded-sm">Cancelar</Button>
          <Button onClick={submit} disabled={busy || !form.license_plate || !form.operario_id || !form.wash_type}
            className="bg-[var(--ag-gold)] hover:bg-[#FFCD3C] rounded-sm" data-testid="ns-submit">
            {busy ? "Guardando…" : "Registrar servicio"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Sprint H Batch 3 — Buscador de empresas existentes en el CRM.
 * Al teclear ≥2 chars, llama a /api/customers?q=... (back ya soporta búsqueda)
 * y muestra hasta 6 resultados filtrados por `type=empresa`. Click → autorellena.
 */
function EmpresaAutocomplete({ value, onPick }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!q || q.length < 2) { setItems([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/customers?q=${encodeURIComponent(q)}`);
        const filtered = (data || []).filter((c) => c.type === "empresa").slice(0, 6);
        setItems(filtered);
      } catch { setItems([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="relative">
      <Label className="text-xs uppercase tracking-wider flex items-center gap-1">
        Buscar empresa en tu CRM
        <span className="text-[10px] text-[var(--ag-text-dim)] normal-case">(autorrelleno fiscal)</span>
      </Label>
      <Input
        value={q}
        onChange={(e)=>{ setQ(e.target.value); setOpen(true); }}
        onFocus={()=>setOpen(true)}
        onBlur={()=>setTimeout(()=>setOpen(false), 200)}
        placeholder="Razón social, NIF o nombre…"
        className="mt-1 rounded-sm" data-testid="ns-empresa-search"
      />
      {open && items.length > 0 && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded-sm shadow-lg max-h-56 overflow-auto" data-testid="ns-empresa-suggestions">
          {items.map((c) => (
            <button key={c.id} type="button" onClick={()=>{ onPick(c); setQ(""); setItems([]); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-[var(--ag-midnight)] border-b border-[var(--ag-border)] last:border-b-0"
              data-testid={`ns-empresa-suggest-${c.id}`}>
              <div className="text-sm font-semibold text-[var(--ag-text)] truncate">{c.razon_social || c.name}</div>
              <div className="text-[11px] text-[var(--ag-text-dim)] truncate">
                {c.nif_cif ? <span className="font-mono-tab">{c.nif_cif}</span> : "—"}
                {c.address_fiscal ? ` · ${c.address_fiscal}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
